import {useState} from "react";
import Carousel from 'react-multi-carousel';
import 'react-multi-carousel/lib/styles.css';
import {Link} from "react-router-dom";
import Seo from "../Utils/Seo";
import BookNowModal from "./include/BookNowModal";

const responsive = {
    desktop: {
        breakpoint: {max: 3000, min: 1024}, items: 3, slidesToSlide: 1 // optional, default to 1.
    }, tablet: {
        breakpoint: {max: 1024, min: 464}, items: 2, slidesToSlide: 1 // optional, default to 1.
    }, mobile: {
        breakpoint: {max: 464, min: 0}, items: 1, slidesToSlide: 1 // optional, default to 1.
    }
};

const SITE = "https://tigerrefrigeratedtransport.com";
const serviceAreas = [
    "Dubai",
    "Abu Dhabi",
    "Sharjah",
    "Ajman",
    "Ras Al Khaimah",
    "Fujairah",
    "Umm Al Quwain",
    "Al Ain",
];
const servedIndustries = [
    "Frozen food distribution",
    "Fresh produce and dairy delivery",
    "Meat and seafood transport",
    "Pharmaceutical cold chain",
    "Hotel, restaurant and catering supply",
    "Supermarket and retail replenishment",
];
const arabicKeywords = [
    "نقل مبرد في الإمارات",
    "تأجير شاحنات مبردة دبي",
    "نقل فريزر في أبوظبي",
    "نقل تشيلر في الشارقة",
    "سيارة مبردة للإيجار في الإمارات",
    "خدمات سلسلة التبريد",
];

function Home() {
    const [bookModal, setBookModal] = useState({open: false, note: ""});
    const openBook = (note) => setBookModal({open: true, note});
    const closeBook = () => setBookModal({open: false, note: ""});

    const homeFaqItems = [
        {
            question: "Do you provide refrigerated transport across the UAE?",
            answer: "Yes. Tiger Refrigerated Transport runs chiller vans, freezer vehicles, and refrigerated trucks on routes across the UAE, including Dubai, Abu Dhabi, Sharjah, and other key corridors for temperature-controlled cargo.",
        },
        {
            question: "Which emirates and cities do you cover?",
            answer: "We regularly serve Dubai, Abu Dhabi, Sharjah, and wider UAE logistics routes. Tell us your pickup and drop-off points when you book so we can confirm timing and vehicle type.",
        },
        {
            question: "How do I book a chiller van or freezer truck?",
            answer: "Use Book now on this site to call or choose WhatsApp. Share load type, temperature range, dates, and addresses in the UAE so we can quote and schedule the right refrigerated or mover vehicle.",
        },
        {
            question: "What cargo suits your refrigerated transport?",
            answer: "Typical loads include frozen and chilled food, dairy, meat and seafood, beverages, pharmaceuticals and other goods that need a stable cold chain from collection to delivery.",
        },
    ];

    const homeSchema = {
        "@context": "https://schema.org",
        "@graph": [
            {
                "@type": "LocalBusiness",
                "@id": `${SITE}/#business`,
                name: "Tiger Refrigerated Transport",
                alternateName: [
                    "Tiger Refrigerated Transport UAE",
                    "تايجر للنقل المبرد",
                ],
                description:
                    "Refrigerated transport, chiller van rental, freezer truck rental, and cold-chain delivery across Dubai, Abu Dhabi, Sharjah, and the UAE.",
                image: `${SITE}/assets/img/logo/logo-2.png`,
                url: `${SITE}/`,
                telephone: "+971506168540",
                address: {
                    "@type": "PostalAddress",
                    addressLocality: "Abu Dhabi",
                    addressCountry: "AE",
                },
                geo: {
                    "@type": "GeoCoordinates",
                    latitude: "24.4539",
                    longitude: "54.3773",
                },
                areaServed: serviceAreas.map((city) => ({
                    "@type": "City",
                    name: city,
                })),
                serviceType: [
                    "Refrigerated transport",
                    "Chiller van rental",
                    "Freezer truck rental",
                    "Cold chain logistics",
                    "Temperature-controlled delivery",
                    "Refrigerated truck rental",
                    "نقل مبرد في الإمارات",
                    "تأجير شاحنات مبردة",
                ],
                priceRange: "AED",
                inLanguage: ["en-AE", "ar-AE"],
            },
            {
                "@type": "Service",
                "@id": `${SITE}/#refrigerated-transport-service`,
                serviceType: "Refrigerated transport in the UAE",
                provider: {
                    "@id": `${SITE}/#business`,
                },
                areaServed: serviceAreas.map((city) => ({
                    "@type": "City",
                    name: city,
                })),
                hasOfferCatalog: {
                    "@type": "OfferCatalog",
                    name: "UAE cold chain transport services",
                    itemListElement: [
                        {
                            "@type": "Offer",
                            itemOffered: {
                                "@type": "Service",
                                name: "Chiller van rental Dubai and UAE",
                            },
                        },
                        {
                            "@type": "Offer",
                            itemOffered: {
                                "@type": "Service",
                                name: "Freezer truck rental UAE",
                            },
                        },
                        {
                            "@type": "Offer",
                            itemOffered: {
                                "@type": "Service",
                                name: "Temperature-controlled delivery for food and pharmaceuticals",
                            },
                        },
                    ],
                },
            },
            {
                "@type": "FAQPage",
                "@id": `${SITE}/#faq`,
                mainEntity: homeFaqItems.map(({ question, answer }) => ({
                    "@type": "Question",
                    name: question,
                    acceptedAnswer: {
                        "@type": "Answer",
                        text: answer,
                    },
                })),
            },
        ],
    };

    return (<>
        <Seo
            title="Refrigerated Transport UAE | Dubai, Abu Dhabi, Ajman Chiller Trucks"
            description="Reliable refrigerated transport in Dubai, Abu Dhabi, Ajman, Sharjah and across the UAE with chiller vans, freezer trucks, refrigerated trucks and temperature-controlled cold-chain delivery."
            path="/"
            keywords={`refrigerated transport UAE, refrigerated transport Dubai, refrigerated transport Abu Dhabi, refrigerated transport Ajman, refrigerated transport Sharjah, chiller van rental Dubai, chiller truck rental Abu Dhabi, freezer truck rental UAE, refrigerated truck rental UAE, cold chain logistics UAE, reefer truck UAE, temperature controlled transport UAE, ${arabicKeywords.join(", ")}`}
            schema={homeSchema}
        />

        {/* Home-only hero tweaks: do not edit global theme CSS; scoped to this page + desktop breakpoint */}
        <style>{`
@media (min-width: 992px) {
  .home-page-hero-override.ca-hero-4 {
    height: 1000px;
    clip-path: polygon(0 0, 100% 0, 100% 86%, 50% 100%, 0 86%);
  }
  .home-page-hero-override.ca-hero-4 .ca-hero-4-content {
    padding-top: 155px;
    position: relative;
    z-index: 2;
  }
  .home-page-hero-override.ca-hero-4 .ca-hero-4-btn {
    position: relative;
    z-index: 3;
  }
}
        `}</style>

        <div className="ca-hero-4 home-page-hero-override p-relative z-index-1 fix">
            <div className="ca-hero-4-shape p-absolute">
                <img src="assets/img/shape/ca-dot-shape4.1.png" alt=""/>
            </div>
            <div className="container">
                <div className="row">
                    <div className="col-lg-6">
                        <div
                            className="ca-hero-4-content wow tpFadeInUp"
                            data-wow-duration="1.5s"
                            data-wow-delay=".1s"
                        >
                            <h5 className="ca-section-subtitle subtitle-bg-8 p-relative theme-black-4 br-50">
                                Refrigerated transport UAE
                            </h5>
                            <h1 className="ca-slider-heading text-white pt-20 pb-20 ca-text-cap">
                                Trusted refrigerated transport for Dubai, Abu Dhabi & the UAE
                            </h1>
                            <p className="pb-32">
                                Tiger Refrigerated Transport keeps your cold chain moving with chiller vans, freezer
                                vehicles, and refrigerated trucks on UAE routes — dependable temperature-controlled
                                delivery when timing and product quality matter.
                            </p>

                            {/* btn */}
                            <div className="ca-hero-4-btn">
                                <Link
                                    to="/services"
                                    className="ca-btn-primary ca-btn-primary-4 theme-bg-4 text-white br-50"
                                >
                                    Our Service
                                    <span>
                <i className="fa-solid fa-arrow-right"/>
              </span>
                                </Link>
                            </div>
                        </div>
                    </div>
                    <div className="col-lg-6"></div>
                </div>
            </div>
            <div className="ca-hero-4-img">
                <img className="w-100" src="assets/img/slider/ca-hero-4.png" alt=""/>
            </div>
        </div>

        <div className="ca-circle-text text-center">
            <img
                className="circle-text-animation"
                src="assets/img/slider/circle-text.png"
                alt=""
            />
            <div className="circle-ic-4">
                <img src="assets/img/icon/circle-ic.svg" alt=""/>
            </div>
        </div>

        <section className="ca-about-3 pt-100 pb-70">
            <div className="container">
                <div className="row align-items-center">
                    <div className="col-lg-6">
                        <div className="ca-abo-item-1">
                            <div className="row">
                                <div className="col-lg-6 col-md-6">
                                    {/* single about item */}
                                    <div className="ca-abo-single-item mb-30">
                                        <img
                                            className="w-100 lar-img-1"
                                            src="/assets/img/froozen-fish.jpeg"
                                            alt=""
                                        />
                                        <div className="ca-abo-hover-item">
                                            <div className="ca-abo-content">
                                                <div className="ca-abo-icon">
                              <span>
                                <img
                                    src="/assets/img/icon/ca-delivery3.1.svg"
                                    alt=""
                                />
                              </span>
                                                </div>
                                                <p>Delivered Goods</p>
                                            </div>
                                        </div>
                                    </div>
                                    {/* single about item */}
                                    <div className="ca-abo-single-item mb-30">
                                        <img
                                            className="w-100 sm-img-1"
                                            src="/assets/img/fruit-and-vegetable.jpeg"
                                            alt=""
                                        />
                                        <div className="ca-abo-hover-item">
                                            <div className="ca-abo-content">
                                                <div className="ca-abo-icon">
                              <span>
                                <img
                                    src="/assets/img/icon/ca-delivery3.1.svg"
                                    alt=""
                                />
                              </span>
                                                </div>

                                                <p>Delivered Goods</p>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                                <div className="col-lg-6 col-md-6">
                                    {/* single about item */}
                                    <div className="ca-abo-single-item mb-30">
                                        <img
                                            className="w-100 sm-img-2"
                                            src="/assets/img/Meat.jpeg"
                                            alt=""
                                        />
                                        <div className="ca-abo-hover-item">
                                            <div className="ca-abo-content">
                                                <div className="ca-abo-icon">
                              <span>
                                <img
                                    src="/assets/img/icon/ca-delivery3.1.svg"
                                    alt=""
                                />
                              </span>
                                                </div>

                                                <p>Delivered Goods</p>
                                            </div>
                                        </div>
                                    </div>
                                    {/* single about item */}
                                    <div className="ca-abo-single-item mb-30">
                                        <img
                                            className="w-100 lar-img-2"
                                            src="/assets/img/about/ca-about-3.3.png"
                                            alt=""
                                        />
                                        <div className="ca-abo-hover-item">
                                            <div className="ca-abo-content">
                                                <div className="ca-abo-icon">
                              <span>
                                <img
                                    src="/assets/img/icon/ca-delivery3.1.svg"
                                    alt=""
                                />
                              </span>
                                                </div>
                                                <h3 className="ca-abo-hover-title">
                                                    <span className="counter">11</span>K+
                                                </h3>
                                                <p>Delivered Goods</p>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                    <div className="col-xl-6 mb-30">
                        <div className="ca-ab-content3">
                            <div className="ca-about-content-3 ca-sec-content-3">
                                <h5 className="ca-section-subtitle subtitle-bg-6 p-relative theme-color-3 br-50">
                                    Why Choose Us
                                </h5>
                                <h2 className="ca-section-title theme-black-3 fnw-600 pt-16 ca-text-cap">
                                    Reliable and Innovative <br/> Refrigerated Transport
                                </h2>
                                <p className="pt-16 pb-32">
                                    With years of experience in refrigerated transport, we lead the industry by offering
                                    innovative and reliable transport solutions. Our team ensures your products are
                                    delivered with the highest standards of care and efficiency.
                                </p>
                            </div>
                            <div className="ca-ab-item-check">
                                {/* single-item */}
                                <div className="ca-ab-sngle-item">
                                    <div className="ca-ab-sngle-item-ic">
          <span>
            <i className="fa-solid fa-check"/>
          </span>
                                    </div>
                                    <div className="ca-ab-sngle-item-content">
                                        <h4 className="ca-item-ch-title">
                                            Experts in Refrigerated Transport
                                        </h4>
                                    </div>
                                </div>
                                {/* single-item */}
                                <div className="ca-ab-sngle-item">
                                    <div className="ca-ab-sngle-item-ic">
          <span>
            <i className="fa-solid fa-check"/>
          </span>
                                    </div>
                                    <div className="ca-ab-sngle-item-content">
                                        <h4 className="ca-item-ch-title">
                                            Leaders in Cold Transport Services
                                        </h4>
                                    </div>
                                </div>
                                {/* single-item */}
                                <div className="ca-ab-sngle-item">
                                    <div className="ca-ab-sngle-item-ic">
          <span>
            <i className="fa-solid fa-check"/>
          </span>
                                    </div>
                                    <div className="ca-ab-sngle-item-content">
                                        <h4 className="ca-item-ch-title">
                                            Cutting-Edge Technology for Efficiency
                                        </h4>
                                    </div>
                                </div>
                                {/* single-item */}
                                <div className="ca-ab-sngle-item">
                                    <div className="ca-ab-sngle-item-ic">
          <span>
            <i className="fa-solid fa-check"/>
          </span>
                                    </div>
                                    <div className="ca-ab-sngle-item-content">
                                        <h4 className="ca-item-ch-title">
                                            Committed to Fresh and Timely Deliveries
                                        </h4>
                                    </div>
                                </div>
                            </div>
                            <div className="ca-about-item3 cream-bg-3 p-relative">
                                <p>
                                    Partner with us to experience seamless refrigerated transport solutions tailored to
                                    your business needs. We are committed to delivering excellence every step of the
                                    way.
                                </p>
                            </div>
                            <div className="ca-about-3-btn">
                                <Link to="/services" className="ca-btn-primary-3 theme-bg-3 text-white br-50">
                                    Learn More
                                    <span>
          <i className="fa-solid fa-angle-right"/>
        </span>
                                </Link>
                            </div>
                        </div>
                    </div>

                </div>
            </div>
        </section>

        <section className="ca-service-4 cream-bg-3 pt-100 pb-70 p-relative z-index-1">
            <div className="ca-servic-4-shape p-absolute">
                <img src="assets/img/shape/ca-plane-1.1.png" alt=""/>
            </div>
            <div className="ca-servic-4-shape-2 p-absolute">
                <img src="assets/img/shape/ca-ft-shape4.1.png" alt=""/>
            </div>
            <div className="container">
                <div className="ca-service-content-4 text-center mb-60 aos-init aos-animate">
                    <h5 className="ca-section-subtitle subtitle-bg-8 p-relative theme-black-4 br-50">
                        Our Service
                    </h5>
                    <h2 className="ca-section-title theme-black-4 fnw-600 pt-16 ca-text-cap">
                        Freezer, Chiller and Mover Services
                    </h2>
                    <p className="pt-16">
                        We specialize in transporting temperature-sensitive goods, ensuring your freezer and chiller cargo
                        remain fresh and safe
                        <br/> throughout the entire transport journey.
                    </p>
                </div>
                <div className="row">
                    <Carousel
                        swipeable={true}
                        draggable={true}
                        showDots={true}
                        responsive={responsive}
                        ssr={true} // means to render carousel on server-side.
                        infinite={true}
                        autoPlaySpeed={1000}
                        keyBoardControl={true}
                        customTransition="transform 300ms ease-in-out"
                        transitionDuration={300}
                        containerClass="carousel-container"
                        removeArrowOnDeviceType={["mobile"]}
                        deviceType={["desktop", "tablet", "mobile"]}
                        dotListClass="custom-dot-list-style"
                    >
                        <div className="ca-service-4-item br-7 fix p-relative z-index-1 mb-30">
                            <img
                                className="w-100"
                                src="/assets/img/vehicle/car-1.jpg"
                                alt=""
                            />
                            <div className="ca-servic-4-ic-box p-absolute">
                                <div className="ca-service-4-icon">
                  <span>
                    <img src="assets/img/icon/ca-service-ic-4.1.svg" alt=""/>
                  </span>
                                </div>
                                <div className="ca-service-4-content">
                                    <h3 className="ca-ser-4-title">
                                        <a href="#" tabIndex={0}>
                                            Freezer Van Transport
                                        </a>
                                    </h3>
                                    <p>
                                        Dedicated freezer vans for frozen food, meat, and dairy with stable
                                        temperature control from pickup to delivery.
                                    </p>
                                    <div className="d-flex flex-column align-items-stretch gap-2 mt-2 w-100">
                                        <a href="#" className="read-mor4" tabIndex={0}>
                                            Read More{" "}
                                            <span>
                      <i className="fa-solid fa-angle-right"/>
                    </span>
                                        </a>
                                        <button
                                            type="button"
                                            className="ca-btn-primary-3 theme-bg-3 text-white br-50 text-center py-2 border-0"
                                            style={{minHeight: "44px", cursor: "pointer"}}
                                            onClick={() => openBook("Freezer van transport — Dubai & UAE")}
                                        >
                                            Book now
                                        </button>
                                    </div>
                                </div>
                            </div>
                            <div className="ca-ser-overlay p-absolute">
                                <img
                                    className="w-100"
                                    src="assets/img/service/ca-overlay-servic-4.png"
                                    alt=""
                                />
                            </div>
                        </div>
                        <div className="ca-service-4-item br-7 fix p-relative z-index-1 mb-30">
                            <img
                                className="w-100"
                                src="/assets/img/vehicle/car-4.jpg"
                                alt=""
                            />
                            <div className="ca-servic-4-ic-box p-absolute">
                                <div className="ca-service-4-icon">
                  <span>
                    <img src="assets/img/icon/ca-service-ic-4.3.svg" alt=""/>
                  </span>
                                </div>
                                <div className="ca-service-4-content">
                                    <h3 className="ca-ser-4-title">
                                        <a href="#" tabIndex={0}>
                                            12 Ton Mover Transport
                                        </a>
                                    </h3>
                                    <p>
                                        Reliable 12 ton mover transport for heavy cargo and commercial
                                        shifting with careful handling and on-time UAE delivery.
                                    </p>
                                    <div className="d-flex flex-column align-items-stretch gap-2 mt-2 w-100">
                                        <a href="#" className="read-mor4" tabIndex={0}>
                                            Read More{" "}
                                            <span>
                      <i className="fa-solid fa-angle-right"/>
                    </span>
                                        </a>
                                        <button
                                            type="button"
                                            className="ca-btn-primary-3 theme-bg-3 text-white br-50 text-center py-2 border-0"
                                            style={{minHeight: "44px", cursor: "pointer"}}
                                            onClick={() => openBook("12 ton mover transport — Dubai & UAE")}
                                        >
                                            Book now
                                        </button>
                                    </div>
                                </div>
                            </div>
                            <div className="ca-ser-overlay p-absolute">
                                <img
                                    className="w-100"
                                    src="assets/img/service/ca-overlay-servic-4.png"
                                    alt=""
                                />
                            </div>
                        </div>
                        <div className="ca-service-4-item br-7 fix p-relative z-index-1 mb-30">
                            <img
                                className="w-100"
                                src="/assets/img/vehicle/car-2.jpg"
                                alt=""
                            />
                            <div className="ca-servic-4-ic-box p-absolute">
                                <div className="ca-service-4-icon">
                  <span>
                    <img src="assets/img/icon/ca-service-ic-4.1.svg" alt=""/>
                  </span>
                                </div>
                                <div className="ca-service-4-content">
                                    <h3 className="ca-ser-4-title">
                                        <a href="#" tabIndex={0}>
                                            Refrigerated Truck Delivery
                                        </a>
                                    </h3>
                                    <p>
                                        Professional refrigerated truck transport for larger shipments requiring
                                        temperature consistency and safe handling.
                                    </p>
                                    <div className="d-flex flex-column align-items-stretch gap-2 mt-2 w-100">
                                        <a href="#" className="read-mor4" tabIndex={0}>
                                            Read More{" "}
                                            <span>
                      <i className="fa-solid fa-angle-right"/>
                    </span>
                                        </a>
                                        <button
                                            type="button"
                                            className="ca-btn-primary-3 theme-bg-3 text-white br-50 text-center py-2 border-0"
                                            style={{minHeight: "44px", cursor: "pointer"}}
                                            onClick={() => openBook("Refrigerated truck delivery — Dubai & UAE")}
                                        >
                                            Book now
                                        </button>
                                    </div>
                                </div>
                            </div>
                            <div className="ca-ser-overlay p-absolute">
                                <img
                                    className="w-100"
                                    src="assets/img/service/ca-overlay-servic-4.png"
                                    alt=""
                                />
                            </div>
                        </div>
                        <div className="ca-service-4-item br-7 fix p-relative z-index-1 mb-30">
                            <img
                                className="w-100"
                                src="/assets/img/vehicle/car-3.jpg"
                                alt=""
                            />
                            <div className="ca-servic-4-ic-box p-absolute">
                                <div className="ca-service-4-icon">
                  <span>
                    <img src="assets/img/icon/ca-service-ic-4.2.svg" alt=""/>
                  </span>
                                </div>
                                <div className="ca-service-4-content">
                                    <h3 className="ca-ser-4-title">
                                        <a href="#" tabIndex={0}>
                                            Frozen Cargo Transport
                                        </a>
                                    </h3>
                                    <p>
                                        End-to-end frozen cargo movement with controlled cooling and dependable
                                        transport across Dubai and the UAE.
                                    </p>
                                    <div className="d-flex flex-column align-items-stretch gap-2 mt-2 w-100">
                                        <a href="#" className="read-mor4" tabIndex={0}>
                                            Read More{" "}
                                            <span>
                      <i className="fa-solid fa-angle-right"/>
                    </span>
                                        </a>
                                        <button
                                            type="button"
                                            className="ca-btn-primary-3 theme-bg-3 text-white br-50 text-center py-2 border-0"
                                            style={{minHeight: "44px", cursor: "pointer"}}
                                            onClick={() => openBook("Frozen cargo transport — Dubai & UAE")}
                                        >
                                            Book now
                                        </button>
                                    </div>
                                </div>
                            </div>
                            <div className="ca-ser-overlay p-absolute">
                                <img
                                    className="w-100"
                                    src="assets/img/service/ca-overlay-servic-4.png"
                                    alt=""
                                />
                            </div>
                        </div>
                        <div className="ca-service-4-item br-7 fix p-relative z-index-1 mb-30">
                            <img
                                className="w-100"
                                src="/assets/img/vehicle/mover-6ton.jpg"
                                alt=""
                            />
                            <div className="ca-servic-4-ic-box p-absolute">
                                <div className="ca-service-4-icon">
                  <span>
                    <img src="assets/img/icon/ca-service-ic-4.2.svg" alt=""/>
                  </span>
                                </div>
                                <div className="ca-service-4-content">
                                    <h3 className="ca-ser-4-title">
                                        <a href="#" tabIndex={0}>
                                            6 Ton Mover Truck
                                        </a>
                                    </h3>
                                    <p>
                                        6 ton mover for office, warehouse, and heavy-item transportation with
                                        professional loading and timely delivery.
                                    </p>
                                    <div className="d-flex flex-column align-items-stretch gap-2 mt-2 w-100">
                                        <a href="#" className="read-mor4" tabIndex={0}>
                                            Read More{" "}
                                            <span>
                      <i className="fa-solid fa-angle-right"/>
                    </span>
                                        </a>
                                        <button
                                            type="button"
                                            className="ca-btn-primary-3 theme-bg-3 text-white br-50 text-center py-2 border-0"
                                            style={{minHeight: "44px", cursor: "pointer"}}
                                            onClick={() => openBook("6 ton mover truck — Dubai & UAE")}
                                        >
                                            Book now
                                        </button>
                                    </div>
                                </div>
                            </div>
                            <div className="ca-ser-overlay p-absolute">
                                <img
                                    className="w-100"
                                    src="assets/img/service/ca-overlay-servic-4.png"
                                    alt=""
                                />
                            </div>
                        </div>
                        <div className="ca-service-4-item br-7 fix p-relative z-index-1 mb-30">
                            <img
                                className="w-100"
                                src="/assets/img/vehicle/mover-9ton.jpg"
                                alt=""
                            />
                            <div className="ca-servic-4-ic-box p-absolute">
                                <div className="ca-service-4-icon">
                  <span>
                    <img src="assets/img/icon/ca-service-ic-4.3.svg" alt=""/>
                  </span>
                                </div>
                                <div className="ca-service-4-content">
                                    <h3 className="ca-ser-4-title">
                                        <a href="#" tabIndex={0}>
                                            9 Ton Mover Truck
                                        </a>
                                    </h3>
                                    <p>
                                        9 ton mover for large cargo shifting and industrial transport with
                                        secure handling across UAE routes.
                                    </p>
                                    <div className="d-flex flex-column align-items-stretch gap-2 mt-2 w-100">
                                        <a href="#" className="read-mor4" tabIndex={0}>
                                            Read More{" "}
                                            <span>
                      <i className="fa-solid fa-angle-right"/>
                    </span>
                                        </a>
                                        <button
                                            type="button"
                                            className="ca-btn-primary-3 theme-bg-3 text-white br-50 text-center py-2 border-0"
                                            style={{minHeight: "44px", cursor: "pointer"}}
                                            onClick={() => openBook("9 ton mover truck — Dubai & UAE")}
                                        >
                                            Book now
                                        </button>
                                    </div>
                                </div>
                            </div>
                            <div className="ca-ser-overlay p-absolute">
                                <img
                                    className="w-100"
                                    src="assets/img/service/ca-overlay-servic-4.png"
                                    alt=""
                                />
                            </div>
                        </div>
                    </Carousel>
                </div>
            </div>
        </section>

        <section className="pt-90 pb-70" aria-labelledby="coverage-heading">
            <div className="container">
                <div className="row align-items-start">
                    <div className="col-lg-6 mb-30">
                        <div className="ca-service-content-4">
                            <h5 className="ca-section-subtitle subtitle-bg-8 p-relative theme-black-4 br-50">
                                UAE Coverage
                            </h5>
                            <h2 id="coverage-heading" className="ca-section-title theme-black-4 fnw-600 pt-16 ca-text-cap">
                                Chiller, freezer and refrigerated transport across the UAE
                            </h2>
                            <p className="pt-16">
                                We help UAE businesses move chilled and frozen cargo with temperature-controlled vans and trucks for city delivery, intercity routes, scheduled supply runs, and urgent cold-chain dispatch.
                            </p>
                            <div className="row pt-20">
                                {serviceAreas.map((area) => (
                                    <div className="col-sm-6 mb-3" key={area}>
                                        <div className="ca-ab-sngle-item">
                                            <div className="ca-ab-sngle-item-ic">
                                                <span>
                                                    <i className="fa-solid fa-check"/>
                                                </span>
                                            </div>
                                            <div className="ca-ab-sngle-item-content">
                                                <h4 className="ca-item-ch-title">{area}</h4>
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                    <div className="col-lg-6 mb-30">
                        <div className="ca-ser-icon-box ca-ser-icon-box-2 fix p-relative cream-bg-3 z-index-1 p-32 br-7">
                            <h3 className="ca-title fnw-700 pb-16">Industries we serve</h3>
                            <p className="pb-20">
                                Our refrigerated vehicles support the UAE businesses that depend on stable cold-chain
                                delivery every day, from food suppliers to pharmacies, hotels, caterers, supermarkets
                                and distributors.
                            </p>
                            <div className="d-flex flex-column gap-3">
                                {servedIndustries.map((industry) => (
                                    <div key={industry} className="d-flex align-items-start gap-3">
                                        <span className="theme-color-3">
                                            <i className="fa-solid fa-snowflake"/>
                                        </span>
                                        <span>{industry}</span>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </section>

        <section className="cream-bg-3 pt-80 pb-80 z-index-1 p-relative" id="faq" aria-labelledby="faq-heading">
            <div className="container">
                <div className="ca-service-content-4 text-center mb-50">
                    <h5 className="ca-section-subtitle subtitle-bg-8 p-relative theme-black-4 br-50">
                        Questions
                    </h5>
                    <h2 id="faq-heading" className="ca-section-title theme-black-4 fnw-600 pt-16 ca-text-cap">
                        Refrigerated transport in the UAE
                    </h2>
                    <p className="pt-16">
                        Straight answers for customers searching for cold-chain and refrigerated logistics across Dubai
                        and the UAE.
                    </p>
                </div>
                <div className="row justify-content-center">
                    <div className="col-lg-10">
                        <div className="d-flex flex-column gap-4">
                            {homeFaqItems.map((item) => (
                                <div
                                    key={item.question}
                                    className="ca-ser-icon-box ca-ser-icon-box-2 fix p-relative cream-bg-3 z-index-1 p-32 br-7 theme-black-bg-3"
                                >
                                    <h3 className="ca-title fnw-600 text-white pb-12" style={{ fontSize: "1.15rem" }}>
                                        {item.question}
                                    </h3>
                                    <p className="mb-0 text-white" style={{ opacity: 0.92 }}>
                                        {item.answer}
                                    </p>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            </div>
        </section>

        <section className="video-play-section">
            <div className="container">
                <div className="ca-video-img br-7 p-relative z-index-1">
                    <img className="w-100" src="/assets/img/vehicle/car-1.jpg" alt="Refrigerated transport fleet ready for service"/>
                    <a href="/assets/video/video-tigertransport.mp4" className="video-play-button" target="_blank"
                       rel="noopener noreferrer">
                        <span className="play-icon">▶</span>
                    </a>
                </div>
            </div>
        </section>


        <div className="ca-working-section theme-black-bg-3 pt-390 pb-70">
            <div className="container">
                <div
                    className="ca-working-section-3 ca-sec-content-3 text-center mb-60 aos-init aos-animate"
                    data-aos="fade-up"
                    data-aos-duration={800}
                >
                    <h5 className="ca-section-subtitle subtitle-bg-7 p-relative text-white br-50">
                        Working Process
                    </h5>
                    <h2 className="ca-section-title theme-black-3 fnw-600 text-white pt-16">
                        Efficient Cold Chain Process
                    </h2>
                    <p className="pt-16">
                        Our refrigerated transport process is designed to maintain temperature-sensitive goods, ensuring
                        reliable delivery from the start to the final destination.
                    </p>
                </div>
                <div className="row">
                    {/* working icon box */}
                    <div className="col-xl-3 col-lg-4 col-md-6 mb-30">
                        <div
                            className="ca-work-ic-box br-7 aos-init aos-animate"
                            data-aos="fade-up"
                            data-aos-duration={800}
                        >
                            <div className="ca-work-ic-box-img">
                        <span>
                            <img src="/assets/img/icon/ca-work-1.1.svg" alt="Coordination & Execution"/>
                        </span>
                            </div>
                            <div className="ca-work-ic-box-content">
                                <h4 className="ca-title fnw-600 text-white pt-24 pb-16">
                                    <a href="service-single.html">Coordination & Execution</a>
                                </h4>
                                <p>
                                    We coordinate the entire cold transport process, leveraging our transport expertise to
                                    ensure your refrigerated goods are handled with care.
                                </p>
                            </div>
                        </div>
                    </div>
                    {/* working icon box */}
                    <div className="col-xl-3 col-lg-4 col-md-6 mb-30">
                        <div
                            className="ca-work-ic-box br-7 aos-init aos-animate"
                            data-aos="fade-up"
                            data-aos-duration={1000}
                        >
                            <div className="ca-work-ic-box-img">
                        <span>
                            <img src="/assets/img/icon/ca-work-1.2.svg" alt="Planning and Strategy"/>
                        </span>
                            </div>
                            <div className="ca-work-ic-box-content">
                                <h4 className="ca-title fnw-600 text-white pt-24 pb-16">
                                    <a href="service-single.html">Planning & Strategy</a>
                                </h4>
                                <p>
                                    Our team develops a tailored strategy for each shipment, ensuring that your
                                    refrigerated goods are stored and transported under optimal conditions.
                                </p>
                            </div>
                        </div>
                    </div>
                    {/* working icon box */}
                    <div className="col-xl-3 col-lg-4 col-md-6 mb-30">
                        <div
                            className="ca-work-ic-box br-7 aos-init aos-animate"
                            data-aos="fade-up"
                            data-aos-duration={1200}
                        >
                            <div className="ca-work-ic-box-img">
                        <span>
                            <img src="/assets/img/icon/ca-work1.3.svg" alt="Real-Time Monitoring"/>
                        </span>
                            </div>
                            <div className="ca-work-ic-box-content">
                                <h4 className="ca-title fnw-600 text-white pt-24 pb-16">
                                    <a href="service-single.html">Real-Time Monitoring</a>
                                </h4>
                                <p>
                                    We track your refrigerated goods in real-time, providing updates to ensure
                                    transparency throughout the journey.
                                </p>
                            </div>
                        </div>
                    </div>
                    {/* working icon box */}
                    <div className="col-xl-3 col-lg-4 col-md-6 mb-30">
                        <div
                            className="ca-work-ic-box br-7 aos-init aos-animate"
                            data-aos="fade-up"
                            data-aos-duration={1400}
                        >
                            <div className="ca-work-ic-box-img">
                        <span>
                            <img src="/assets/img/icon/ca-work1.4.svg" alt="Secure Transportation"/>
                        </span>
                            </div>
                            <div className="ca-work-ic-box-content">
                                <h4 className="ca-title fnw-600 text-white pt-24 pb-16">
                                    <a href="service-single.html">Secure Transportation</a>
                                </h4>
                                <p>
                                    We ensure safe and timely delivery of your temperature-sensitive goods, giving you
                                    peace of mind and maintaining product integrity.
                                </p>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <section className="ca-portfilo-2 pt-100 pb-70">
            <div className="container">
                <div
                    className="ca-portfolio-content-3 ca-sec-content-3 text-center mb-60 aos-init aos-animate"
                    data-aos="fade-up"
                    data-aos-duration={800}
                >
                    <h5 className="ca-section-subtitle subtitle-bg-6 p-relative theme-color-3 br-50">
                        Our Services
                    </h5>
                    <h2 className="ca-section-title theme-black-3 fnw-600 pt-16">
                        Shifting Refrigeration Items with Care
                    </h2>
                    <p className="pt-16">
                        Our expertise lies in shifting all types of refrigerator items with
                        precision and care. Whether it’s a small fridge or large commercial units,
                        <br/> we ensure that your items are transported safely and efficiently.
                    </p>
                </div>
                <div className="row">
                    {/* single project item */}
                    <div className="col-lg-3 col-md-6">
                        <div className="ca-single-pro-img-3 mb-30">
                            <img
                                className="w-100"
                                src="/assets/img/portfolio/ca-portfoli3.3.png"
                                alt="Fridge Transport"
                            />
                            <div className="ca-project-hover-item1">
                                <a
                                    href="project-single.html"
                                    className="ca-project-hover-item1-icon"
                                >
                            <span>
                                <img src="/assets/img/icon/ca-arow-3.1.svg" alt=""/>
                            </span>
                                </a>
                                <div className="ca-project-hover-item-content">
                                    <h4 className="ca-project-hov-sub-title">Refrigerator Transport</h4>
                                    <h3 className="ca-project-hov-title">
                                        <a href="project-single.html">
                                            Safe Loading and Unloading
                                        </a>
                                    </h3>
                                    <p>
                                        We handle each refrigerator item with care, ensuring safe loading and unloading
                                        at every step.
                                    </p>
                                </div>
                            </div>
                        </div>
                    </div>
                    {/* single project item */}
                    <div className="col-lg-4 col-md-6">
                        <div className="ca-single-pro-img-3 mb-30">
                            <img
                                className="w-100"
                                src="/assets/img/portfolio/ca-portfolio-3.5.png"
                                alt="Fridge Transport"
                            />
                            <div className="ca-project-hover-item1">
                                <a
                                    href="project-single.html"
                                    className="ca-project-hover-item1-icon"
                                >
                            <span>
                                <img src="/assets/img/icon/ca-arow-3.1.svg" alt=""/>
                            </span>
                                </a>
                                <div className="ca-project-hover-item-content">
                                    <h4 className="ca-project-hov-sub-title">Temperature Control</h4>
                                    <h3 className="ca-project-hov-title">
                                        <a href="project-single.html">
                                            Maintaining Cold Chain Integrity
                                        </a>
                                    </h3>
                                    <p>
                                        Our transport solutions ensure that refrigerator items maintain their
                                        temperature integrity throughout the journey.
                                    </p>
                                </div>
                            </div>
                        </div>
                    </div>
                    {/* single project item */}
                    <div className="col-lg-5 col-md-6">
                        <div className="ca-single-pro-img-3 mb-30">
                            <img
                                className="w-100"
                                src="/assets/img/portfolio/ca-port2.3.png"
                                alt="Fridge Transport"
                            />
                            <div className="ca-project-hover-item1">
                                <a
                                    href="project-single.html"
                                    className="ca-project-hover-item1-icon"
                                >
                            <span>
                                <img src="/assets/img/icon/ca-arow-3.1.svg" alt=""/>
                            </span>
                                </a>
                                <div className="ca-project-hover-item-content">
                                    <h4 className="ca-project-hov-sub-title">Timely Delivery</h4>
                                    <h3 className="ca-project-hov-title">
                                        <a href="project-single.html">
                                            Fast & Reliable Transport
                                        </a>
                                    </h3>
                                    <p>
                                        Your refrigerator items will be delivered on time, ensuring minimal disruption
                                        to your operations.
                                    </p>
                                </div>
                            </div>
                        </div>
                    </div>
                    {/* single project item */}
                    <div className="col-lg-6 col-md-6">
                        <div className="ca-single-pro-img-3 mb-30">
                            <img
                                className="w-100 pro-lar-img-1"
                                src="/assets/img/portfolio/ca-project3.4.png"
                                alt="Fridge Transport"
                            />
                            <div className="ca-project-hover-item1">
                                <a
                                    href="project-single.html"
                                    className="ca-project-hover-item1-icon"
                                >
                            <span>
                                <img src="/assets/img/icon/ca-arow-3.1.svg" alt=""/>
                            </span>
                                </a>
                                <div className="ca-project-hover-item-content">
                                    <h4 className="ca-project-hov-sub-title">Custom Packaging</h4>
                                    <h3 className="ca-project-hov-title">
                                        <a href="project-single.html">
                                            Tailored Solutions for Safe Transport
                                        </a>
                                    </h3>
                                    <p>
                                        We offer custom packaging solutions for refrigerator items, ensuring they remain
                                        secure throughout the transportation process.
                                    </p>
                                </div>
                            </div>
                        </div>
                    </div>
                    {/* single project item */}
                    <div className="col-lg-6 col-md-6">
                        <div className="ca-single-pro-img-3 mb-30">
                            <img
                                className="w-100 pro-lar-img-1"
                                src="/assets/img/portfolio/ca-port2.3.png"
                                alt="Fridge Transport"
                            />
                            <div className="ca-project-hover-item1">
                                <a
                                    href="project-single.html"
                                    className="ca-project-hover-item1-icon"
                                >
                            <span>
                                <img src="/assets/img/icon/ca-arow-3.1.svg" alt=""/>
                            </span>
                                </a>
                                <div className="ca-project-hover-item-content">
                                    <h4 className="ca-project-hov-sub-title">Expert Handling</h4>
                                    <h3 className="ca-project-hov-title">
                                        <a href="project-single.html">
                                            Professional Team You Can Trust
                                        </a>
                                    </h3>
                                    <p>
                                        Our team of experts will handle your refrigerator items with utmost care and
                                        professionalism.
                                    </p>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </section>

        <BookNowModal
            open={bookModal.open}
            onClose={closeBook}
            heading="Book now"
            note={bookModal.note || undefined}
        />

    </>);
}

export default Home;
